import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import {
  Box,
  TextField,
  FormControl,
  CircularProgress,
  Grid,
  Autocomplete,
  FormControlLabel,
  Switch,
  FormHelperText,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import Locations from "examples/ReuseFunctions/Locations";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      "Invalid email format"
    )
    .typeError("Enter a valid email")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Enter a valid phone number")
    .required("Phone number is required"),
  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  region_id: Yup.string().required("Area is required"),
  address: Yup.string().required("Address is required"),
  skill_set: Yup.string().required("Skills are required"),
  role_id: Yup.array()
    .of(Yup.number().typeError("Invalid role ID"))
    .min(1, "At least one role is required")
    .required("Role is required"),
});

// Types
interface State {
  state_id: number;
  state_name: string;
}

interface City {
  city_id: number;
  city_name: string;
}

interface Region {
  region_id: number;
  region_name: string;
}

interface Role {
  role_id: number;
  role_name: string;
}

interface Employee {
  state_id: number;
  city_id: number;
  region_id: number;
  name: string;
  email: string;
  skill_set: string;
  address: string;
  phone: string;
  is_active: number | boolean;
  roles: Role[];
  role_id: number;
  division_ids?: string[];
}

interface Divisions {
  division_name: string;
  division_id: string;
  customer_name: string;
  branch_code: string;
}

export interface EditEmployeeProps {
  id?: string;
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EditEmployee: React.FC<EditEmployeeProps> = ({
  id: idProp,
  embedded,
  onSuccess,
  onCancel,
}) => {
  const { id: idFromParams } = useParams();
  const id = idProp ?? idFromParams;
  const { states, cities, areas, setSelectedState, setSelectedCity } =
    Locations();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialState, setInitialState] = useState<State | null>(null);
  const [initialCity, setInitialCity] = useState<City | null>(null);
  const [initialRegion, setInitialRegion] = useState<Region | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [divisions, setDivisions] = useState<Divisions[]>([]);
  const [companyIds, setCompanyIds] = useState<string[]>([]);

  const { fetchError, fetchSuccess } = useSnackbar();

  const BRANCH_HEAD_ROLE_ID = 7;
  const SUPERVISOR_ROLE_ID = 3;

  const fetchEmployee = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/employee/${id}`);
      const { data } = response;
      setSelectedState(data?.state_id);
      setSelectedCity(data?.city_id);
      setInitialState({
        state_id: data?.state_id,
        state_name: data?.state_name,
      });
      setInitialCity({
        city_id: data?.city_id,
        city_name: data?.city_name,
      });
      setInitialRegion({
        region_id: data?.region_id,
        region_name: data?.region_name,
      });

      setEmployee(data);

      // Set division IDs if they exist
      if (data?.division_ids && Array.isArray(data.division_ids)) {
        setCompanyIds(data.division_ids);
        console.log("Loaded division IDs:", data.division_ids); // Debug log
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      fetchError("Failed to fetch employee details");
      throw error;
    }
  }, [id, setSelectedState, setSelectedCity, fetchError]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  useEffect(() => {
    if (employee) {
      setSelectedState(employee?.state_id);
      setSelectedCity(employee?.city_id);
    }
  }, [employee, setSelectedState, setSelectedCity]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesData = await axios.get("/roles/all");
        const allRoles = rolesData?.data?.list || [];
        const filteredRoles = allRoles.filter(
          (role: { role_name: string }) =>
            role.role_name !== "superAdmin" && role.role_name !== "customer"
        );
        setRoles(filteredRoles);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
      }
    };
    fetchRoles();
  }, []);

  // Fetch all divisions on component mount
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const res = await axios.get("/divisions/all");
        const list = res.data.list || [];
        setDivisions(list);
      } catch (err) {
        console.error("Failed to fetch divisions:", err);
        fetchError("Unable to fetch divisions");
      }
    };

    fetchDivisions();
  }, [fetchError]);

  if (loading) return <CircularProgress />;

  // Check if current selected roles include Branch Head or Supervisor
  const shouldShowDivisions = (selectedRoleIds: number[]) => {
    return selectedRoleIds.some(
      (roleId) =>
        roleId === BRANCH_HEAD_ROLE_ID || roleId === SUPERVISOR_ROLE_ID
    );
  };

  const formContent = (
    <Box sx={{ width: "100%", ...(!embedded && { p: 3, borderRadius: 2, boxShadow: 2 }) }}>
      {!embedded && (
        <SoftTypography variant="h5" style={{ marginBottom: 18 }}>
          Edit Employee
        </SoftTypography>
      )}
      <Formik
          enableReinitialize
          initialValues={{
            name: employee?.name || "",
            email: employee?.email || "",
            phone: employee?.phone || "",
            state_id: employee?.state_id || "",
            city_id: employee?.city_id || "",
            region_id: employee?.region_id || "",
            address: employee?.address || "",
            skill_set: employee?.skill_set || "",
            role_id: employee?.roles?.map((r) => r.role_id) || [],
            is_active: employee?.is_active === 1 || false,
            division_id: companyIds || [],
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            const { state_id, city_id, ...rest } = values;
            try {
              const response = await axios.put(`/employee/${id}`, { ...rest });
              if (response.status === 200) {
                fetchSuccess("Employee updated successfully");
                if (embedded && onSuccess) onSuccess();
                else navigate("/employees-list");
              }
            } catch (err) {
              if (err instanceof AxiosError && err.response) {
                fetchError(err.response.data.error);
              }
            }
          }}
        >
          {({ errors, touched, handleChange, setFieldValue, values }) => (
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Name
                    </SoftTypography>
                    <TextField
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.name && touched.name}
                      helperText={touched.name && errors.name}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Phone Number
                    </SoftTypography>
                    <TextField
                      name="phone"
                      value={values.phone}
                      inputProps={{ maxLength: 10 }}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.phone && touched.phone}
                      helperText={touched.phone && errors.phone}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Email
                    </SoftTypography>
                    <TextField
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.email && touched.email}
                      helperText={touched.email && errors.email}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Role
                    </SoftTypography>
                    <Autocomplete
                      multiple
                      options={roles || []}
                      getOptionLabel={(option) => option.role_name || ""}
                      value={roles.filter((role) =>
                        Array.isArray(values.role_id)
                          ? values.role_id.includes(role.role_id)
                          : values.role_id === role.role_id
                      )}
                      onChange={(event, newValue) => {
                        const newRoleIds = newValue.map((role) => role.role_id);
                        setFieldValue("role_id", newRoleIds);

                        // Clear division_id if Branch Head or Supervisor is removed
                        if (!shouldShowDivisions(newRoleIds)) {
                          setFieldValue("division_id", []);
                          setCompanyIds([]);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="role_id"
                          variant="outlined"
                          error={!!errors.role_id && touched.role_id}
                          helperText={touched.role_id && errors.role_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                {shouldShowDivisions(values.role_id) && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Select Divisions
                      </SoftTypography>
                      <Autocomplete
                        multiple
                        options={divisions}
                        getOptionLabel={(option) =>
                          `${option.division_name} - ${option.customer_name} - ${option.branch_code}`
                        }
                        value={divisions.filter((c) =>
                          companyIds.includes(c.division_id)
                        )}
                        onChange={(event, newValue) => {
                          const newDivisionIds = newValue.map(
                            (c) => c.division_id
                          );
                          setCompanyIds(newDivisionIds);
                          setFieldValue("division_id", newDivisionIds);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          name="is_active"
                          checked={values.is_active}
                          onChange={(event) => {
                            setFieldValue("is_active", event.target.checked);
                          }}
                          color="primary"
                        />
                      }
                      label={
                        <SoftTypography variant="caption" fontWeight="bold">
                          Status
                        </SoftTypography>
                      }
                    />
                    {touched.is_active && errors.is_active && (
                      <FormHelperText error>{errors.is_active}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      State
                    </SoftTypography>
                    <Autocomplete
                      id="state-autocomplete"
                      options={states ?? []}
                      getOptionLabel={(option) => option.state_name || ""}
                      value={values.state_id ? initialState : null}
                      onChange={(event, newValue) => {
                        setFieldValue("city_id", "");
                        setFieldValue("region_id", "");
                        setFieldValue("state_id", newValue?.state_id || "");
                        setInitialState(
                          newValue
                            ? {
                                state_id: newValue?.state_id,
                                state_name: newValue?.state_name,
                              }
                            : null
                        );
                        setSelectedState(newValue ? newValue.state_id : null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="state_id"
                          variant="outlined"
                          error={!!errors.state_id && touched.state_id}
                          helperText={touched.state_id && errors.state_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      City
                    </SoftTypography>
                    <Autocomplete
                      options={cities ?? []}
                      getOptionLabel={(option) => option.city_name || ""}
                      value={values.city_id ? initialCity : null}
                      onChange={(event, newValue) => {
                        setFieldValue("region_id", "");
                        setFieldValue("city_id", newValue?.city_id || "");
                        setInitialCity(
                          newValue
                            ? {
                                city_id: newValue?.city_id,
                                city_name: newValue?.city_name,
                              }
                            : null
                        );
                        setSelectedCity(newValue ? newValue.city_id : null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="city_id"
                          variant="outlined"
                          error={!!errors.city_id && touched.city_id}
                          helperText={touched.city_id && errors.city_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Area
                    </SoftTypography>
                    <Autocomplete
                      options={areas ?? []}
                      getOptionLabel={(option) => option.region_name || ""}
                      value={values.region_id ? initialRegion : null}
                      onChange={(event, newValue) => {
                        setFieldValue("region_id", newValue?.region_id || "");
                        setInitialRegion(
                          newValue
                            ? {
                                region_id: newValue.region_id,
                                region_name: newValue.region_name,
                              }
                            : null
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="region_id"
                          variant="outlined"
                          error={!!errors.region_id && touched.region_id}
                          helperText={touched.region_id && errors.region_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Address
                    </SoftTypography>
                    <TextField
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.address && touched.address}
                      helperText={touched.address && errors.address}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Skills
                    </SoftTypography>
                    <TextField
                      name="skill_set"
                      value={values.skill_set}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.skill_set && touched.skill_set}
                      helperText={touched.skill_set && errors.skill_set}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item>
                    <SoftButton
                      className="success-btn"
                      variant="gradient"
                      color="success"
                      type="submit"
                    >
                      Update
                    </SoftButton>
                  </Grid>
                  <Grid item>
                    <SoftButton
                      className="cancel-btn"
                      variant="gradient"
                      color="warning"
                      onClick={() =>
                        embedded && onCancel ? onCancel() : navigate("/employees-list")
                      }
                    >
                      Cancel
                    </SoftButton>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
    </Box>
  );

  if (embedded) return formContent;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {formContent}
    </DashboardLayout>
  );
};


export default EditEmployee;
